import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import {
    getAllUsersByProjectId,
    updateManagementTask,
    getServiceProviders,
    getMyVendors,
    addManagementPermissionQuery
} from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { childDueAfterParent, parentDueDate, plannedEndFromDuration } from './taskTree';
import DueDateReasonModal from './DueDateReasonModal';
import AssigneePicker from './AssigneePicker';
import {
    ASSIGNEE_REQUIRED_MESSAGE,
    assigneePayload,
    hasAssignee,
    subtaskStaffUserOptions
} from './assigneeKind';
import { allocationExceedsBudget, ALLOCATION_EXCEEDED_MESSAGE } from './budget';
import {
    usesPermissionFlow,
    selectableSubtaskTypeOptions,
    SubTaskPermissionType,
    SUBTASK_TYPE_REQUIRED_MESSAGE
} from './permission';

const CreateSubtaskModal = ({
    onClose,
    parentTask,
    initialTitle = '',
    initialDescription = '',
    initialPermissionType = '',
    initialPermissionQueryId = '',
    // Raise a new authority query and its reply subtask in one submit. The
    // description is the query text.
    newQuery = false
}) => {
    // "permission" here means the permission flow — compliance included.
    const isPermission = usesPermissionFlow(parentTask);
    const fromQueryRound =
        newQuery ||
        initialPermissionType === SubTaskPermissionType.QUERY_RAISED ||
        Boolean(initialPermissionQueryId);
    const typeOptions = selectableSubtaskTypeOptions({ queryRaised: fromQueryRound });
    const queryClient = useQueryClient();
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        defaultValues: {
            title: initialTitle,
            description: initialDescription,
            assignedTo_id: '',
            assigneeKind: 'employee',
            assignedTo_sp_id: '',
            assignedTo_vendor_id: '',
            approver_id: '',
            permissionType: newQuery ? SubTaskPermissionType.QUERY_RAISED : initialPermissionType,
            priority: 'Medium',
            duration: 1,
            budget: '',
            plannedStartDate: '',
            docs: []
        }
    });

    const { fields: docFields, append: appendDoc, remove: removeDoc } = useFieldArray({
        control,
        name: "docs"
    });

    const projectId = parentTask?.project_id;
    const { data: usersData } = useQuery({
        queryKey: ['projectUsers', projectId],
        queryFn: () => getAllUsersByProjectId(projectId, 1, 500),
        enabled: !!projectId
    });
    const usersOptions = subtaskStaffUserOptions(usersData?.data);

    const { data: spData } = useQuery({
        queryKey: ['serviceProviders'],
        queryFn: getServiceProviders
    });

    const spAssigneeOptions = spData?.data?.map(sp => ({
        label: `${sp.user?.name || 'SP'} (${sp.category?.name || ''})`,
        value: sp.id
    })) || [];

    const { data: vendorData } = useQuery({
        queryKey: ['myVendors'],
        queryFn: () => getMyVendors(1, 200)
    });
    const vendorList = vendorData?.data?.data || vendorData?.data || [];
    const vendorOptions = (Array.isArray(vendorList) ? vendorList : [])
        .filter((v) => v.user_id || v.user?.id)
        .map((v) => ({
            label: v.businessName || v.name || v.user?.name || `Vendor #${v.id}`,
            value: v.id
        }));



    const updateMutation = useMutation({
        mutationFn: async (payload) => {
            if (!newQuery) return updateManagementTask(parentTask.id, payload);
            // Add the query first: the response is the updated task, so the
            // new query is its last row, and the reply subtask links to it.
            const text = String(payload.subtasks[0].description || '').trim();
            const response = await addManagementPermissionQuery(parentTask.id, text);
            const rows = response?.data?.permissionQueries || [];
            const created = rows[rows.length - 1];
            return updateManagementTask(parentTask.id, {
                subtasks: payload.subtasks.map((s) => ({ ...s, permissionQueryId: created?.id }))
            });
        },
        onSuccess: () => {
            toast.success(newQuery ? 'Query and reply subtask added' : 'Subtask added successfully');
            queryClient.invalidateQueries(['managementTask', String(parentTask.id)]);
            onClose();
        },
        onError: (error) => {
            // If the query was saved but the subtask failed, it stays open
            // with Create reply on the Query rounds tab.
            if (newQuery) queryClient.invalidateQueries(['managementTask', String(parentTask.id)]);
            toast.error(error?.response?.data?.message || 'Failed to add subtask');
        }
    });

    const onSubmit = (data) => {
        if (!hasAssignee(data.assigneeKind, data)) {
            toast.error(ASSIGNEE_REQUIRED_MESSAGE);
            return;
        }
        if (isPermission && !data.permissionType) {
            toast.error(SUBTASK_TYPE_REQUIRED_MESSAGE);
            return;
        }
        const siblings = (parentTask.subTask || [])
            .filter((s) => !s.parent_subtask_id)
            .map((s) => ({ parent_subtask_id: null, budget: s.budget }));
        siblings.push({ parent_subtask_id: null, budget: data.budget });
        if (allocationExceedsBudget(parentTask.budget, siblings, null)) {
            toast.error(ALLOCATION_EXCEEDED_MESSAGE);
            return;
        }
        const plannedEndDate = plannedEndFromDuration(data.plannedStartDate, data.duration);
        if (!plannedEndDate) {
            toast.error('Enter a start date and a duration of at least 1 day');
            return;
        }
        const newSubtask = {
            title: data.title,
            description: data.description,
            ...assigneePayload(data.assigneeKind, data),
            project_id: parentTask.project_id,
            approver_id: data.approver_id ? Number(data.approver_id) : null,
            permissionType: isPermission
                ? (fromQueryRound ? SubTaskPermissionType.QUERY_RAISED : data.permissionType)
                : undefined,
            permissionQueryId: initialPermissionQueryId || undefined,
            duration: Number(data.duration),
            budget: data.budget === '' || data.budget == null ? null : Number(data.budget),
            // No status: the backend opens it Planned and the assignee
            // moves it on with Start.
            plannedStartDate: data.plannedStartDate,
            plannedEndDate,
            docs: data.docs || [],
            parent_subtask_id: null
        };

        // Only the new subtask: the PUT never deletes omitted subtasks, and
        // echoing existing ones rewrites them. No parent kind / permission fields.
        const payload = { subtasks: [newSubtask] };

        if (childDueAfterParent(plannedEndDate, parentDueDate(parentTask, null))) {
            setPendingPayload(payload);
            setShowReasonModal(true);
            return;
        }

        updateMutation.mutate(payload);
    };

    const assignedToId = watch('assignedTo_id');
    const computedEndDate = plannedEndFromDuration(watch('plannedStartDate'), watch('duration'));

    // Filter approver options to exclude the assigned user
    const approverOptions = [
        { label: 'No Approver', value: '' },
        ...usersOptions.filter(user => user.value != assignedToId)
    ];

    useEffect(() => {
        const currentApprover = watch('approver_id');
        if (assignedToId && currentApprover == assignedToId) {
            setValue('approver_id', ''); // Reset approver if it matches the new assignee
        }
    }, [assignedToId, setValue, watch]);

    return (
        <>
        <Modal
            name={newQuery ? 'Add Query' : 'Add Subtask'}
            width={60}
            onClose={onClose}
            hideClose={false}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                    <FormInput
                        label="Title"
                        register={register}
                        register_key="title"
                        type="text"
                        isRequired={true}
                        width={100}
                    />
                    <FormInput
                        label={newQuery ? 'Authority query' : 'Description'}
                        register={register}
                        register_key="description"
                        type="text"
                        placeholder={newQuery ? 'What the authority asked for' : undefined}
                        isRequired={true}
                        width={100}
                    />
                    <AssigneePicker
                        register={register}
                        watch={watch}
                        setValue={setValue}
                        usersOptions={usersOptions}
                        spOptions={spAssigneeOptions}
                        vendorOptions={vendorOptions}
                    />
                    <FormInput
                        label="Approver"
                        register={register}
                        register_key="approver_id"
                        type="dropdown"
                        options={approverOptions}
                        isRequired={false}
                        width={100}
                    />
                    {isPermission && (
                        <FormInput
                            label="Type"
                            register={register}
                            register_key="permissionType"
                            type="dropdown"
                            options={typeOptions}
                            isRequired={true}
                            readOnly={fromQueryRound}
                            width={100}
                        />
                    )}
                    <FormInput
                        label="Allocation (₹)"
                        register={register}
                        register_key="budget"
                        type="number"
                        width={100}
                    />
                    <FormInput
                        label="Duration (Days)"
                        register={register}
                        register_key="duration"
                        type="number"
                        isRequired={true}
                        width={100}
                        CustomFeature={{ min: 1 }}
                    />
                    <FormInput
                        label="Start Date"
                        register={register}
                        register_key="plannedStartDate"
                        type="date"
                        isRequired={true}
                        width={100}
                    />
                    {/* Derived from start date + duration, so it is shown in the
                        same shell as FormInput's date field but never edited. */}
                    <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700 w-full">
                        <div className="flex items-start space-x-2 w-fit">
                            <span className="text-gray-600">End Date</span>
                            <span className="text-xs text-gray-400 self-center">(auto)</span>
                        </div>
                        <input
                            type="date"
                            value={computedEndDate || ''}
                            readOnly
                            tabIndex={-1}
                            aria-label="End date, calculated from start date and duration"
                            title="Calculated from start date and duration"
                            className="w-full p-2 border-[1px] border-stone-300 rounded bg-[#e7e5e4] text-gray-700 cursor-not-allowed"
                        />
                    </div>
                </div>


                <div className="border-t pt-2">
                    <div className="flex justify-between items-center mb-2">
                        <h5 className="text-sm font-semibold text-gray-600">Documents</h5>
                        <button
                            type="button"
                            onClick={() => appendDoc({ name: '', url: '' })}
                            className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                        >
                            + Add Doc
                        </button>
                    </div>
                    {docFields.map((doc, index) => (
                        <div key={doc.id} className="grid grid-cols-2 gap-2 mb-2 items-end">
                            <FormInput
                                label="Doc Name"
                                register={register}
                                register_key={`docs.${index}.name`}
                                type="text"
                                placeholder="e.g. Spec Sheet"
                                isRequired={true}
                                width={100}
                            />
                            <div className="flex gap-2 items-end">
                                <FormInput
                                    label="Doc URL"
                                    register={register}
                                    register_key={`docs.${index}.url`}
                                    type="text"
                                    placeholder="https://..."
                                    isRequired={true}
                                    width={100}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeDoc(index)}
                                    className="text-red-500 hover:text-red-700 mb-2"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                        type="button"
                        onClick={onClose}
                        className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={updateMutation.isPending}
                        className="bg-[#346C75] text-white px-8 py-2 rounded hover:bg-[#2a565e] disabled:opacity-50"
                    >
                        {newQuery ? 'Add Query' : 'Add Subtask'}
                    </Button>
                </div>
            </form>
        </Modal>
        {showReasonModal && (
            <DueDateReasonModal
                title="Child due date after parent"
                body="This due date is after the parent due date. Enter a reason to override."
                onClose={() => {
                    setShowReasonModal(false);
                    setPendingPayload(null);
                }}
                onConfirm={(reason) => {
                    if (!pendingPayload) return;
                    const subtasks = [...pendingPayload.subtasks];
                    const last = { ...subtasks[subtasks.length - 1], dueDateOverrideReason: reason };
                    subtasks[subtasks.length - 1] = last;
                    setShowReasonModal(false);
                    setPendingPayload(null);
                    updateMutation.mutate({ ...pendingPayload, subtasks });
                }}
            />
        )}
        </>
    );
};

export default CreateSubtaskModal;
